import { useContext } from "react";
import { Box, Button } from "@mui/material";
import { Layer } from "../../types/layer";
import { DrawingContext, DrawingMode, DrawingFeatureType } from "../../contexts/DrawingContext";
import LayerControlItem from "./LayerControlItem";
import HomeIcon from "@mui/icons-material/Home";
import { useAuth } from "../../contexts/AuthContext";
import { isViewOnly } from "../../utils/permissions";

interface AssetsControlProps {
  layer: Layer | null;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

const AssetsControl: React.FC<AssetsControlProps> = ({ layer, expanded, onExpandedChange }) => {
  const { drawingMode, setDrawingMode, setDrawingFeatureType } = useContext(DrawingContext);

  const { user } = useAuth();
  const viewOnly = isViewOnly(user);
  if (!layer) return null;

  const handleToggleVisibility = () => {
    layer.toolbarButton?.props?.onClick?.();
  };

  const handleDrawClick = (featureType: string) => {
    if (drawingMode === DrawingMode.ASSET) {
      setDrawingMode(null);
      setDrawingFeatureType(null);
    } else {
      setDrawingMode(DrawingMode.ASSET);
      setDrawingFeatureType(featureType);
    }
  };

  const drawingContent = viewOnly ? null : (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Button
        size="small"
        variant={drawingMode === DrawingMode.ASSET ? "contained" : "outlined"}
        onClick={() => handleDrawClick(DrawingFeatureType.POLYGON)}
        sx={{
          color: drawingMode === DrawingMode.ASSET ? "#fff" : "#00d4ad",
          borderColor: "#00d4ad",
          backgroundColor: drawingMode === DrawingMode.ASSET ? "#00d4ad" : "transparent",
          "&:hover": {
            backgroundColor: drawingMode === DrawingMode.ASSET ? "#00b899" : "rgba(0, 212, 173, 0.1)",
          },
        }}
      >
        Draw Polygon
      </Button>
      <Button
        size="small"
        variant={drawingMode === DrawingMode.ASSET ? "contained" : "outlined"}
        onClick={() => handleDrawClick(DrawingFeatureType.LINE)}
        sx={{
          color: drawingMode === DrawingMode.ASSET ? "#fff" : "#00d4ad",
          borderColor: "#00d4ad",
          backgroundColor: drawingMode === DrawingMode.ASSET ? "#00d4ad" : "transparent",
          "&:hover": {
            backgroundColor: drawingMode === DrawingMode.ASSET ? "#00b899" : "rgba(0, 212, 173, 0.1)",
          },
        }}
      >
        Draw Line
      </Button>
      <Button
        size="small"
        variant={drawingMode === DrawingMode.ASSET ? "contained" : "outlined"}
        onClick={() => handleDrawClick(DrawingFeatureType.POINT)}
        sx={{
          color: drawingMode === DrawingMode.ASSET ? "#fff" : "#00d4ad",
          borderColor: "#00d4ad",
          backgroundColor: drawingMode === DrawingMode.ASSET ? "#00d4ad" : "transparent",
          "&:hover": {
            backgroundColor: drawingMode === DrawingMode.ASSET ? "#00b899" : "rgba(0, 212, 173, 0.1)",
          },
        }}
      >
        Draw Point
      </Button>
      <Button
        size="small"
        variant={drawingMode === DrawingMode.ASSET ? "contained" : "outlined"}
        onClick={() => handleDrawClick(DrawingFeatureType.UPLOAD)}
        sx={{
          color: drawingMode === DrawingMode.ASSET ? "#fff" : "#00d4ad",
          borderColor: "#00d4ad",
          backgroundColor: drawingMode === DrawingMode.ASSET ? "#00d4ad" : "transparent",
          "&:hover": {
            backgroundColor: drawingMode === DrawingMode.ASSET ? "#00b899" : "rgba(0, 212, 173, 0.1)",
          },
        }}
      >
        Upload File
      </Button>
    </Box>
  );

  return (
    <LayerControlItem
      title="Assets"
      icon={<HomeIcon />}
      visible={layer.active ?? false}
      onToggleVisibility={handleToggleVisibility}
      enabled={!!layer.rawSource}
      drawingContent={drawingContent}
      expanded={expanded}
      onExpandedChange={onExpandedChange}
    />
  );
};

export default AssetsControl;
